/* eslint-disable no-magic-numbers */
import React, { useContext } from 'react';
import classNames from 'classnames';

import { FormattedDate } from '@futrli/localisation';
import { UIFeatherIcon } from '@futrli/ui-components';

import { MAX_DIGITS_IN_CELL } from '../../../../core/constants';
import { FormattedAmount } from '../../../../ui/data-grid/components/formatted-amount';
import { forceLocalDate } from '../../../../utils/dates';
import { UserGridSettingsContext } from '../../../detail/reports/user-grid-settings/user-grid-settings-context';

import * as styles from './accounts-impacted.scss';
import { AccountsImpactedLine, DIRECTION, useAccountsImpactedGrid } from './use-accounts-impacted-grid';

interface Props {
  columns: string[];
  impactedLines?: AccountsImpactedLine[];
}

/**
 * Renders impacted-account values with measured fit-or-scroll column sizing.
 */
export const AccountsImpactedGrid = ({ columns, impactedLines }: Props) => {
  const { showDecimals } = useContext(UserGridSettingsContext);
  const { invalidLines, layout, onMouseDown, ref, slide } = useAccountsImpactedGrid({ columns, impactedLines });

  if (!impactedLines || !impactedLines.length) {
    return null;
  }

  return (
    <div style={{ position: 'relative', display: 'flex' }}>
      {layout.isScrollable ? (
        <div
          className={classNames(styles.iconButton, styles.arrowPrevContainer)}
          data-testid="accounts-impacted-previous"
          onClick={() => slide(DIRECTION.back)}
        >
          <UIFeatherIcon iconName="chevron-left" />
        </div>
      ) : null}
      <div style={{ marginTop: 26 }}>
        {impactedLines.map((line, index) => (
          <div
            key={`${line.name}-${index}`}
            className={classNames(styles.accountName, invalidLines[line.name] ? styles.invalid : '')}
          >
            {line.name}
          </div>
        ))}
      </div>
      <div
        ref={ref}
        onMouseDown={onMouseDown}
        className={styles.scrollContainer}
        data-testid="accounts-impacted-viewport"
      >
        {columns.map((c: string, i: number) => {
          const localDate = forceLocalDate(c);

          return (
            <div
              key={c}
              className={styles.scrollItem}
              data-testid="accounts-impacted-column"
              style={{ width: layout.columnWidth }}
            >
              <div className={classNames(styles.label, styles.uppercase, styles.date)}>
                <FormattedDate value={localDate} month="short" year="2-digit" />
              </div>
              {impactedLines.map((line, index) => (
                <div
                  key={`${line.name}-${index}`}
                  className={classNames(styles.numberCell, line.valid?.[i] ? '' : styles.invalidCell)}
                  style={{ width: layout.columnWidth }}
                >
                  <FormattedAmount
                    amount={Number(line.data[i])}
                    maxDigits={MAX_DIGITS_IN_CELL}
                    hideDecimals={!showDecimals}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {layout.isScrollable ? (
        <div
          className={classNames(styles.iconButton, styles.arrowNextContainer)}
          data-testid="accounts-impacted-next"
          onClick={() => slide(DIRECTION.forward)}
        >
          <UIFeatherIcon iconName="chevron-right" />
        </div>
      ) : null}
    </div>
  );
};
